import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
  Signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { _HttpClient } from '@delon/theme';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmailStatusService } from '@shared-feature/components/email-status-modal/email-status.service';
import { SendEmailComponent } from '@shared-feature/components/email-status-modal/send-email/send-email.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE } from '@shared-feature/components/email-status-modal/send-email-mapping';
import { NzTableModule } from 'ng-zorro-antd/table';

export enum TransactionEmailStatus {
  SENT = 1,
  DELIVERED = 2,
  FAILED = 3,
  OPENED = 4
}

interface EmailHistoryStatus {
  id: number;
  emailAddress: string;
  status: TransactionEmailStatus;
  createdAt: string;
}

interface EmailHistoryDetail {
  id: number;
  docType: string;
  docCode: string;
  subject: string;
  message: string;
  receipientEmail: string;
  cc: string;
  bcc: string;
  htmlContent: string;
  reportUrl: string;
  lastStatus: TransactionEmailStatus;
  lastStatusUpdatedAt: string;
  emailHistoryStatuses: EmailHistoryStatus[];
}

interface ProcessedStatus {
  status: TransactionEmailStatus;
  timestamp: Date;
  emailAddresses: string[];
  isInitialSent?: boolean;
  to?: string;
  cc?: string[];
  bcc?: string[];
}

@Component({
  selector: 'app-email-status-modal',
  standalone: true,
  imports: [
    FormsModule,
    NzModalModule,
    NzIconModule,
    NgForOf,
    NgClass,
    NgIf,
    NzButtonComponent,
    DatePipe,
    SendEmailComponent,
    NzSpinModule,
    NzTableModule
  ],
  templateUrl: './email-status-modal.component.html',
  styleUrls: ['./email-status-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailStatusModalComponent {
  #http = inject(_HttpClient);
  #destroyRef = inject(DestroyRef);
  #emailStatusService = inject(EmailStatusService);

  TransactionEmailStatus = TransactionEmailStatus;

  docType = input<string | null>(null);

  gridItems = input<any>(null);

  loading = signal(false);

  customReportType = input<any>(null);

  docCode = computed(() => this.#emailStatusService.selectedDocCode());

  allEmails = signal<any>([]);

  showAllEmails = signal<boolean>(false);

  id = computed(
    () =>
      this.gridItems()?.find((item: any) => item.docCode === this.docCode())
        ?.id || null
  );

  senderTransactionType: Signal<any> = computed(() => {
    const docTypeValue = this.docType();
    return docTypeValue
      ? (REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE[docTypeValue.toLowerCase()] ??
          null)
      : null;
  });

  constructor() {
    effect(() => {
      if (this.docType() && this.docCode()) {
        this.getEmailHistory();
      }
    });
  }

  showSendEmailModal = signal(false);

  readonlyMode = signal(false);

  emailHistoryDetail = signal<EmailHistoryDetail | null>(null);

  onCloseSendEmail() {
    this.showSendEmailModal.set(false);
    this.readonlyMode.set(false);
  }

  onShowAll() {
    if (this.showAllEmails()) {
      this.showAllEmails.set(false);
      this.emailHistoryDetail.set(
        this.allEmails()[this.allEmails().length - 1]
      );
    } else {
      this.showAllEmails.set(true);
    }
  }

  onSelectEmail(email: any) {
    this.emailHistoryDetail.set(email);
    this.showAllEmails.set(false);
  }

  processedStatuses = computed(() => {
    const detail = this.emailHistoryDetail();
    if (!detail) return [];

    const statuses: ProcessedStatus[] = [];

    const statusGroups = new Map<
      TransactionEmailStatus,
      EmailHistoryStatus[]
    >();

    detail.emailHistoryStatuses.forEach(status => {
      if (!statusGroups.has(status.status)) {
        statusGroups.set(status.status, []);
      }
      statusGroups.get(status.status)!.push(status);
    });

    // Process SENT status
    if (statusGroups.has(TransactionEmailStatus.SENT)) {
      const sentStatuses = statusGroups.get(TransactionEmailStatus.SENT)!;
      const firstSent = sentStatuses[0];

      statuses.push({
        status: TransactionEmailStatus.SENT,
        timestamp: new Date(firstSent.createdAt),
        emailAddresses: sentStatuses.map(s => s.emailAddress),
        isInitialSent: true,
        to: detail.receipientEmail,
        cc: detail.cc
          ? detail.cc
              .split(',')
              .map(e => e.trim())
              .filter(e => e)
          : [],
        bcc: detail.bcc
          ? detail.bcc
              .split(',')
              .map(e => e.trim())
              .filter(e => e)
          : []
      });
    }

    // Process DELIVERED status
    if (statusGroups.has(TransactionEmailStatus.DELIVERED)) {
      const deliveredStatuses = statusGroups.get(
        TransactionEmailStatus.DELIVERED
      )!;
      const firstDelivered = deliveredStatuses[0];

      statuses.push({
        status: TransactionEmailStatus.DELIVERED,
        timestamp: new Date(firstDelivered.createdAt),
        emailAddresses: deliveredStatuses.map(s => s.emailAddress)
      });
    }

    // Process OPENED status
    if (statusGroups.has(TransactionEmailStatus.OPENED)) {
      const openedStatuses = statusGroups.get(TransactionEmailStatus.OPENED)!;
      const firstOpened = openedStatuses[0];

      statuses.push({
        status: TransactionEmailStatus.OPENED,
        timestamp: new Date(firstOpened.createdAt),
        emailAddresses: openedStatuses.map(s => s.emailAddress)
      });
    }

    // Process FAILED status
    if (statusGroups.has(TransactionEmailStatus.FAILED)) {
      const failedStatuses = statusGroups.get(TransactionEmailStatus.FAILED)!;
      const firstFailed = failedStatuses[0];

      statuses.push({
        status: TransactionEmailStatus.FAILED,
        timestamp: new Date(firstFailed.createdAt),
        emailAddresses: failedStatuses.map(s => s.emailAddress)
      });
    }

    return statuses.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
  });

  getEmailHistory() {
    this.loading.set(true);
    this.#http
      .get(
        `/api/emailHistory/get?docType=${this.docType()}&docCode=${this.docCode()}`
      )
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe((res: any) => {
        this.allEmails.set(res);
        this.emailHistoryDetail.set(res[res.length - 1]);
      });
  }

  close = output<void>();

  onCancel(): void {
    this.close.emit();
  }

  getStatusClass(status: TransactionEmailStatus): string {
    switch (status) {
      case TransactionEmailStatus.SENT:
        return 'sent';
      case TransactionEmailStatus.DELIVERED:
        return 'delivered';
      case TransactionEmailStatus.OPENED:
        return 'opened';
      case TransactionEmailStatus.FAILED:
        return 'failed';
      default:
        return 'sent';
    }
  }

  getIconType(status: TransactionEmailStatus): string {
    switch (status) {
      case TransactionEmailStatus.SENT:
        return 'mail';
      case TransactionEmailStatus.DELIVERED:
        return 'check-circle';
      case TransactionEmailStatus.OPENED:
        return 'folder-open';
      case TransactionEmailStatus.FAILED:
        return 'close-circle';
      default:
        return 'mail';
    }
  }

  getStatusTitle(status: TransactionEmailStatus): string {
    switch (status) {
      case TransactionEmailStatus.SENT:
        return 'Sent';
      case TransactionEmailStatus.DELIVERED:
        return 'Delivered';
      case TransactionEmailStatus.OPENED:
        return 'Opened';
      case TransactionEmailStatus.FAILED:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  onDownloadEmail(): void {
    const detail = this.emailHistoryDetail();
    if (detail?.reportUrl) {
      window.open(detail.reportUrl, '_blank');
    }
  }

  onViewEmail() {
    this.readonlyMode.set(true);
    this.showSendEmailModal.set(true);
  }

  onResendEmail(): void {
    this.showSendEmailModal.set(true);
  }
}
